import React, { useState, useEffect } from 'react';
import { Calendar, Gift, Users, Cake, Phone, Mail } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { customerService } from '../../services/customerService';
import { formatters } from '../../utils/formatters';

const BirthdayCustomers = () => {
  const { user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadBirthdayCustomers();
  }, [user.companyId]);

  const loadBirthdayCustomers = async () => {
    try {
      setLoading(true);
      const allCustomers = await customerService.getByCompany(user.companyId);
      
      const today = new Date();
      const currentMonth = today.getMonth();
      const currentDate = today.getDate();
      
      // Filtrar clientes do mês atual
      const birthdayCustomers = allCustomers
        .filter(customer => {
          if (!customer.birthDate) return false;
          const birthDate = new Date(customer.birthDate);
          return birthDate.getMonth() === currentMonth;
        })
        .map(customer => {
          const birthDate = new Date(customer.birthDate);
          const thisYearBirthday = new Date(today.getFullYear(), birthDate.getMonth(), birthDate.getDate());
          
          // Calcular idade
          const age = today.getFullYear() - birthDate.getFullYear();
          
          // Calcular dias até o aniversário
          let daysUntil;
          if (thisYearBirthday < today) {
            // Aniversário já passou este ano
            const nextYearBirthday = new Date(today.getFullYear() + 1, birthDate.getMonth(), birthDate.getDate());
            daysUntil = Math.ceil((nextYearBirthday - today) / (1000 * 60 * 60 * 24));
          } else {
            daysUntil = Math.ceil((thisYearBirthday - today) / (1000 * 60 * 60 * 24));
          }
          
          return {
            ...customer,
            age,
            daysUntil,
            isToday: daysUntil === 0
          };
        })
        .sort((a, b) => a.daysUntil - b.daysUntil);

      setCustomers(birthdayCustomers);
    } catch (error) {
      console.error('Erro ao carregar aniversariantes:', error);
      setError('Erro ao carregar aniversariantes');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  };

  const getDaysText = (days, isToday) => {
    if (isToday) return 'Hoje!';
    if (days === 1) return 'Amanhã';
    if (days <= 7) return `em ${days} dias`;
    return `em ${days} dias`;
  };

  const currentMonthName = new Date().toLocaleDateString('pt-BR', { month: 'long' });

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-xl shadow-sm border border-pink-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 bg-pink-200 rounded animate-pulse"></div>
          <div className="h-6 bg-pink-200 rounded w-40 animate-pulse"></div>
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-16 bg-pink-100 rounded animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 rounded-xl shadow-sm border border-red-200 p-6">
        <div className="flex items-center gap-3 text-red-600">
          <Calendar className="h-6 w-6" />
          <span className="font-medium">Erro ao carregar aniversariantes</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-xl shadow-lg border border-pink-200 p-6 hover:shadow-xl transition-shadow duration-300">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-r from-pink-500 to-purple-500 rounded-lg">
            <Cake className="h-6 w-6 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-800">
              🎂 Aniversariantes de {currentMonthName}
            </h3>
            <p className="text-sm text-gray-600">
              {customers.length} {customers.length === 1 ? 'pessoa' : 'pessoas'}
            </p>
          </div>
        </div>
        {customers.length > 0 && (
          <div className="text-2xl">🎉</div>
        )}
      </div>

      {customers.length === 0 ? (
        <div className="text-center py-8">
          <Gift className="h-12 w-12 text-pink-300 mx-auto mb-3" />
          <p className="text-gray-600 font-medium">Nenhum aniversariante este mês</p>
          <p className="text-sm text-gray-500 mt-1">
            Que tal criar uma promoção especial para o próximo mês?
          </p>
        </div>
      ) : (
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {customers.map((customer) => (
            <div
              key={customer.id}
              className={`p-4 rounded-lg border-2 transition-all duration-300 hover:scale-102 ${
                customer.isToday
                  ? 'bg-gradient-to-r from-yellow-100 to-orange-100 border-yellow-300 shadow-lg animate-pulse'
                  : customer.daysUntil <= 3
                  ? 'bg-gradient-to-r from-pink-100 to-purple-100 border-pink-300'
                  : 'bg-white border-gray-200 hover:border-pink-300'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-semibold text-gray-800 text-lg">
                      {customer.name}
                    </span>
                    {customer.isToday && (
                      <span className="bg-yellow-500 text-white text-xs px-2 py-1 rounded-full font-bold animate-bounce">
                        HOJE!
                      </span>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600">
                    <div className="flex items-center gap-1">
                      <span>🎈</span>
                      <span className="font-medium">{customer.age} anos</span>
                      <span className="mx-1">•</span>
                      <Calendar className="h-4 w-4" />
                      <span>{formatDate(customer.birthDate)}</span>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-1">
                        <Phone className="h-4 w-4" />
                        <span>{formatters.formatPhone(customer.phone)}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span>💰</span>
                        <span className="font-medium">{customer.points || 0} pts</span>
                      </div>
                    </div>
                    
                    {customer.email && (
                      <div className="flex items-center gap-1 md:col-span-2">
                        <Mail className="h-4 w-4" />
                        <span className="truncate">{customer.email}</span>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="text-right ml-4">
                  <div className={`text-sm font-bold ${
                    customer.isToday 
                      ? 'text-yellow-600' 
                      : customer.daysUntil <= 3 
                      ? 'text-pink-600' 
                      : 'text-gray-600'
                  }`}>
                    {getDaysText(customer.daysUntil, customer.isToday)}
                  </div>
                </div>
              </div>
              
              {customer.isToday && (
                <div className="mt-3 p-2 bg-yellow-200 rounded-md text-center">
                  <span className="text-yellow-800 font-medium text-sm">
                    🎁 Que tal enviar uma mensagem especial ou oferecer um desconto?
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      
      {customers.length > 0 && (
        <div className="mt-4 pt-4 border-t border-pink-200">
          <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
            <Users className="h-4 w-4" />
            <span>Aproveite para fidelizar seus clientes!</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default BirthdayCustomers;
